import { useState } from "react";
import axios from "axios";
import { Eye, EyeSlash } from "@phosphor-icons/react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Login({ onAuthenticated, onSwitchToSignup, onSwitchToForgot }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      // Pinned to the XHR adapter to bypass `fetch` monkey-patching by
      // PostHog session recording on production. PostHog's `fetch` proxy
      // consumes the response body before our code can read it, throwing
      // "body stream already read" on every login → users were stuck on
      // "Connection failed. Please try again." even though the backend
      // returned the correct 200/401.
      const res = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { adapter: "xhr", headers: { "Content-Type": "application/json" } }
      );
      const data = res.data || {};
      if (data.token) {
        localStorage.setItem("id_token", data.token);
        localStorage.setItem("id_user", JSON.stringify(data.user));
        onAuthenticated(data.user);
      } else {
        setError("Invalid email or password");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") {
        setError(detail);
      } else if (err?.response) {
        // Server responded with a non-2xx but no detail string — fall back.
        setError("Invalid email or password");
      } else {
        // Genuine network failure (no response at all).
        setError("Connection failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center" data-testid="login-page">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center gap-3 mb-12">
          <div className="w-12 h-12 bg-[#9FFF00] flex items-center justify-center">
            <span className="text-black font-black text-base">ID</span>
          </div>
          <h1 className="text-2xl font-black tracking-tight text-white">
            INFLUENCE<span className="text-[#9FFF00]">DETECTOR</span>
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
            className="w-full px-4 py-3 bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-[#9FFF00] transition-colors"
            autoFocus
            data-testid="login-email-input"
          />
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full px-4 py-3 pr-12 bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-[#9FFF00] transition-colors"
              data-testid="login-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              tabIndex={-1}
              aria-label={showPassword ? "Hide password" : "Show password"}
              data-testid="login-password-toggle"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-white/40 hover:text-[#9FFF00] transition-colors"
            >
              {showPassword ? <EyeSlash size={18} weight="regular" /> : <Eye size={18} weight="regular" />}
            </button>
          </div>

          {error && (
            <p className="text-red-400 text-sm" data-testid="login-error">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading || !email || !password}
            className="w-full py-3 bg-[#9FFF00] text-black font-bold uppercase tracking-wider hover:bg-[#8AE600] disabled:opacity-50 transition-colors"
            data-testid="login-submit-button"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div className="flex justify-between items-center mt-4">
          <button
            onClick={onSwitchToForgot}
            className="text-white/30 hover:text-white/50 text-sm"
            data-testid="forgot-password-link"
          >
            Forgot password?
          </button>
        </div>

        <p className="text-white/40 text-sm text-center mt-4">
          Don't have an account?{" "}
          <button
            onClick={onSwitchToSignup}
            className="text-[#9FFF00] hover:underline font-medium"
            data-testid="switch-to-signup"
          >
            Start free trial
          </button>
        </p>

        <p className="text-white/20 text-xs text-center mt-8 uppercase tracking-widest">
          Shopify Influencer Detection
        </p>
      </div>
    </div>
  );
}
